import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { apiSlice } from './api';
import { Group, ListParams } from '../types/classroom';
import { ResponseBody } from '../types/common';
import { RootState } from './store';

const groupAdapter = createEntityAdapter({
  selectId: (data: Group) => data.seq,
});
const initialState = groupAdapter.getInitialState({ totalCnt: 0 });

export const groupApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getGroup: builder.query({
      query: ({ page = 0, search = '', size = 30 }: ListParams) => ({
        url: 'library/school/mgmt/info/group',
        params: { page, search, size },
      }),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page && arg.page > 0) {
          currentCache.ids.push(...newItems.ids);
          currentCache.ids = [...new Set(currentCache.ids)];
          currentCache.entities = {
            ...currentCache.entities,
            ...newItems.entities,
          };
        } else {
          return newItems;
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      transformResponse: (res: ResponseBody) => {
        initialState.totalCnt = res.data.totalCnt;
        return groupAdapter.setAll(initialState, res.data.groupList);
      },
      providesTags: (result, error, arg) => {
        return ['Group'];
      },
    }),
    deleteGroup: builder.mutation({
      query: (seq) => ({
        url: `library/school/mgmt/info/group/${seq}`,
        method: 'DELETE',
      }),
    }),
    deleteGroupMember: builder.mutation({
      query: ({ groupSeq, studentSeq }) => ({
        url: `library/school/mgmt/info/group/${groupSeq}/student`,
        method: 'DELETE',
        body: { data: { studentSeq } },
      }),
    }),
    validateGroupName: builder.mutation({
      query: (nm) => ({
        url: `library/school/mgmt/info/group/validate`,
        method: 'POST',
        body: { data: { nm } },
      }),
      transformResponse: (res: ResponseBody) => res.message,
    }),
    addGroup: builder.mutation({
      query: ({ data }) => ({
        url: `library/school/mgmt/info/group`,
        method: 'POST',
        body: { data },
      }),
    }),
    updateGroupName: builder.mutation({
      query: ({ seq, nm }) => ({
        url: `library/school/mgmt/info/group/${seq}`,
        method: 'PUT',
        body: { data: { nm } },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
  }),
});

export const {
  useGetGroupQuery,
  useDeleteGroupMutation,
  useDeleteGroupMemberMutation,
  useValidateGroupNameMutation,
  useAddGroupMutation,
  useUpdateGroupNameMutation,
} = groupApiSlice;

export const selectGroupResult = groupApiSlice.endpoints.getGroup.select({});

const selectGroupData = createSelector(
  selectGroupResult,
  (groupResult) => groupResult.data
);

export const {
  selectAll: selectAllGroup,
  selectById: selectGroupById,
  selectIds: selectGroupIds,
  selectTotal: selectTotalGroupNum,
} = groupAdapter.getSelectors(
  (state: RootState) => selectGroupData(state) ?? initialState
);
